/* PCIS Health — Serviceability (route /supply/pcis-health/serviceability).
   Manage the pincode coverage table for PCIS courier accounts. Three tools:

   1. Upload coverage — pick account(s) → direction → payment → CSV (single
      `pincode` column). Dry-run first (validate + counts, writes nothing), then
      Upload. Pickup and Drop are two separate uploads (different `direction`).
   2. Check a lane — pickup→drop verification; with/without an account.
   3. Account status & clear — is the table loaded for an account (+ row count),
      and clear it (reverts that account to live serviceability checking).

   All calls hit ProShip's pcis-admin/serviceability/pincodes proxy; the envelope's
   meta.message is surfaced on failure (handled in the slice via runPcisEnv).
   Styling follows the other PCIS Health tabs: framed cards, icon-badge section
   heads, 38px fields, MUI gradient/outlined actions. */
import React, {useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {Box, Stack, Typography, Switch, Button, Autocomplete, TextField, Chip} from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined'
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import PageTitle from '../../../components/PageTitle'
import {PageHeader, SearchSelect, FileDrop, Badge, ConfirmDialog, useToast} from '../components'
import {
  useListPcisCourierAccountsQuery,
  useUploadServiceabilityPincodesMutation,
  useLazyCheckServiceabilityLaneQuery,
  useLazyServiceabilityStatusQuery,
  useClearServiceabilityMutation,
} from '../../../redux/supplyApiSlice'

const DIRECTIONS = ['BOTH', 'PICKUP', 'DROP']
const PAYMENTS = ['BOTH', 'PREPAID', 'COD']
const FIELD_H = 38

const cardSx = {border: 1, borderColor: 'divider', borderRadius: '16px', bgcolor: 'background.paper', p: {xs: 2, md: 2.75}, mb: 2.5, boxShadow: '0 1px 2px rgba(15,23,42,.04)'}
const labelSx = {fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, display: 'block'}
const selectSx = {'& .MuiOutlinedInput-root': {height: FIELD_H, py: 0}, '& .MuiOutlinedInput-root .MuiAutocomplete-input': {py: 0}}
const inputSx = {
  width: '100%',
  height: FIELD_H,
  boxSizing: 'border-box',
  border: '1px solid var(--border, #E5E7EB)',
  borderRadius: '6px',
  background: '#fff',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'inherit',
  padding: '0 10px',
  outline: 'none',
  '&:focus': {borderColor: 'var(--primary, #6366F1)'},
}
const primaryBtnSx = {
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 13,
  px: 2.25,
  color: '#fff',
  backgroundImage: 'linear-gradient(135deg, #4F7DF0 0%, #2065D1 100%)',
  boxShadow: '0 6px 16px rgba(32,101,209,.30)',
  '&:hover': {backgroundImage: 'linear-gradient(135deg, #4F7DF0 0%, #2065D1 100%)', filter: 'brightness(1.05)', boxShadow: '0 8px 20px rgba(32,101,209,.4)'},
  '&.Mui-disabled': {backgroundImage: 'none', color: 'rgba(0,0,0,.26)'},
}
const outlinedBtnSx = {borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: 13, px: 2}
const TABS = [
  {label: 'Upload coverage', icon: <CloudUploadOutlinedIcon />},
  {label: 'Check a lane', icon: <FactCheckOutlinedIcon />},
  {label: 'Status & clear', icon: <RuleFolderOutlinedIcon />},
]

/* segmented pill tab bar (icon + label), active pill lifts on white */
function TabBar({value, onChange}) {
  return (
    <Box sx={{display: 'flex', width: '100%', gap: 0.75, p: 0.5, mb: 2.5, borderRadius: '14px', bgcolor: 'var(--row-alt, #F1F3F9)', border: 1, borderColor: 'divider'}}>
      {TABS.map((t, i) => {
        const active = value === i
        return (
          <Box
            key={t.label}
            role='button'
            tabIndex={0}
            onClick={() => onChange(i)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onChange(i)}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              borderRadius: '10px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontSize: 13.5,
              fontWeight: 700,
              userSelect: 'none',
              transition: 'background .15s, color .15s, box-shadow .15s',
              color: active ? '#2065D1' : 'text.secondary',
              bgcolor: active ? '#fff' : 'transparent',
              boxShadow: active ? '0 2px 6px rgba(15,23,42,.12)' : 'none',
              '& svg': {fontSize: 18},
              '&:hover': active ? {} : {color: 'text.primary', bgcolor: 'rgba(15,23,42,.05)'},
            }}
          >
            {t.icon}
            {t.label}
          </Box>
        )
      })}
    </Box>
  )
}

function SectionHead({icon, title, sub}) {
  return (
    <Stack direction='row' alignItems='center' spacing={1.25} sx={{mb: 2.25}}>
      <Box sx={{width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(32,101,209,.1)', color: '#2065D1', flexShrink: 0, '& svg': {fontSize: 18}}}>{icon}</Box>
      <Box>
        <Typography sx={{fontSize: 14.5, fontWeight: 800, lineHeight: 1.2, color: 'text.primary'}}>{title}</Typography>
        {sub ? <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.25}}>{sub}</Typography> : null}
      </Box>
    </Stack>
  )
}

const Labeled = ({label, sx, children}) => (
  <Box sx={sx}>
    <Typography sx={labelSx}>{label}</Typography>
    {children}
  </Box>
)

const pillSx = {display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700, px: '8px', py: '3px', borderRadius: '5px', textTransform: 'uppercase', letterSpacing: '.3px'}
const LaneCell = ({ok, determinable}) =>
  determinable === false ? (
    <Box component='span' sx={{...pillSx, bgcolor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a'}}>Not uploaded</Box>
  ) : ok ? (
    <Box component='span' sx={{...pillSx, bgcolor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0'}}>Yes</Box>
  ) : (
    <Box component='span' sx={{...pillSx, bgcolor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca'}}>No</Box>
  )

export default function PcisServiceability() {
  const toast = useToast()
  const [tab, setTab] = useState(0)
  const uploadedBy = useSelector((s) => s.user?.userInfo?.email || s.user?.userInfo?.userName || '')
  const {data: accountsRaw} = useListPcisCourierAccountsQuery()
  const accounts = useMemo(() => (Array.isArray(accountsRaw) ? accountsRaw : accountsRaw?.data || []), [accountsRaw])
  const accountCodes = useMemo(() => accounts.map((a) => a.account_code).filter(Boolean), [accounts])

  /* ── Upload ── */
  const [selAccounts, setSelAccounts] = useState([])
  const [direction, setDirection] = useState('BOTH')
  const [payment, setPayment] = useState('BOTH')
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [file, setFile] = useState(null)
  const [result, setResult] = useState(null)
  const [uploadServiceability, {isLoading: uploading}] = useUploadServiceabilityPincodesMutation()

  const cpIds = useMemo(() => {
    const set = new Set(accounts.filter((a) => selAccounts.includes(a.account_code)).map((a) => a.cp_id).filter((v) => v != null))
    return Array.from(set)
  }, [accounts, selAccounts])
  const mixedCpId = cpIds.length > 1

  const doUpload = async (dryRun) => {
    if (!selAccounts.length) return toast('Select at least one account.', 'error')
    if (!file) return toast('Attach a pincode CSV first.', 'error')
    if (mixedCpId) return toast('All selected accounts must belong to the same cp_id.', 'error')
    const parts = {file, account_code: selAccounts, direction, payment_mode: payment, replace_existing: String(replaceExisting), dry_run: String(dryRun)}
    if (cpIds.length === 1) parts.cp_id = String(cpIds[0])
    if (uploadedBy) parts.uploaded_by = uploadedBy
    try {
      const res = await uploadServiceability(parts).unwrap()
      setResult({...res, _dryRun: dryRun})
      toast(dryRun ? 'Dry run complete — review the counts below.' : 'Coverage uploaded.', 'success')
    } catch (e) {
      toast(e?.message || 'Upload failed.', 'error')
    }
  }

  /* ── Check a lane ── */
  const [pickup, setPickup] = useState('')
  const [drop, setDrop] = useState('')
  const [checkAccount, setCheckAccount] = useState('')
  const [checkLane, {data: laneRaw, isFetching: checking, error: laneErr}] = useLazyCheckServiceabilityLaneQuery()
  const lane = laneRaw || null
  const runCheck = () => {
    if (!/^\d{6}$/.test(pickup) || !/^\d{6}$/.test(drop)) return toast('Enter valid 6-digit pickup and drop pincodes.', 'error')
    checkLane({pickup, drop, account_code: checkAccount || undefined})
  }

  /* ── Status + clear ── */
  const [statusAccount, setStatusAccount] = useState('')
  const [fetchStatus, {data: statusData, isFetching: statusLoading}] = useLazyServiceabilityStatusQuery()
  const [clearCoverage, {isLoading: clearing}] = useClearServiceabilityMutation()
  const [confirmClear, setConfirmClear] = useState(false)
  const status = statusData || null
  const doClear = async () => {
    try {
      const res = await clearCoverage({account_code: statusAccount}).unwrap()
      toast(`Cleared ${res?.deleted ?? 0} pincodes for ${statusAccount}.`, 'success')
      fetchStatus(statusAccount)
    } catch (e) {
      toast(e?.message || 'Could not clear coverage.', 'error')
    }
    setConfirmClear(false)
  }

  const laneColsSx = {display: 'grid', gridTemplateColumns: 'minmax(180px,1fr) 90px 120px 120px', alignItems: 'center', gap: 1}

  return (
    <>
      <PageTitle>PCIS — Serviceability</PageTitle>
      <PageHeader title='PCIS — Serviceability' sub='Manage the pincode coverage table for Direct Integration (PCIS) courier accounts.' />

      <TabBar value={tab} onChange={setTab} />

      {/* 1 — Upload coverage */}
      {tab === 0 ? (
      <Box sx={cardSx}>
        <SectionHead icon={<CloudUploadOutlinedIcon />} title='Upload coverage' sub='CSV with a single "pincode" column. Upload pickup and drop lists separately by changing Direction.' />

        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '2fr 1fr 1fr'}, gap: 2, mb: 2}}>
          <Labeled label='Accounts'>
            <Autocomplete
              multiple
              size='small'
              options={accountCodes}
              value={selAccounts}
              onChange={(_, v) => setSelAccounts(v)}
              disableCloseOnSelect
              limitTags={3}
              renderTags={(value, getTagProps) => value.map((opt, i) => <Chip size='small' label={opt} {...getTagProps({index: i})} key={opt} />)}
              renderInput={(p) => <TextField {...p} placeholder={selAccounts.length ? '' : 'Search account codes…'} />}
              sx={{'& .MuiOutlinedInput-root': {borderRadius: '6px', fontSize: 13, background: '#fff', minHeight: FIELD_H, py: '2px'}, '& .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--border,#E5E7EB)'}, '& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--primary,#6366F1)'}}}
            />
          </Labeled>
          <Labeled label='Direction'>
            <SearchSelect value={direction} onChange={(v) => setDirection(v || 'BOTH')} options={DIRECTIONS} sx={selectSx} />
          </Labeled>
          <Labeled label='Payment mode'>
            <SearchSelect value={payment} onChange={(v) => setPayment(v || 'BOTH')} options={PAYMENTS} sx={selectSx} />
          </Labeled>
        </Box>

        {mixedCpId ? (
          <Box sx={{fontSize: 12.5, color: '#b45309', bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', p: 1.25, mb: 2}}>
            Selected accounts span multiple cp_ids ({cpIds.join(', ')}). Upload accounts of one cp_id at a time.
          </Box>
        ) : null}

        <Box sx={{mb: 2}}>
          <FileDrop accept='.csv' maxMB={50} title={file ? file.name : 'Drag & drop the pincode CSV, or click to browse'} hint='One 6-digit pincode per line · single "pincode" column · max 50 MB' onFile={(f) => setFile(f)} onError={(m) => toast(m, 'error')} />
        </Box>

        <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap' useFlexGap>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mr: 'auto'}}>
            <Switch size='small' checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} />
            <Typography sx={{fontSize: 12.5, color: 'text.secondary'}}>{replaceExisting ? 'Replace existing (full re-upload)' : 'Merge / add to existing'}</Typography>
          </Box>
          <Button variant='outlined' size='small' startIcon={<PlayArrowOutlinedIcon />} disabled={uploading} onClick={() => doUpload(true)} sx={outlinedBtnSx}>
            {uploading ? 'Working…' : 'Dry run'}
          </Button>
          <Button variant='contained' size='small' disableElevation startIcon={<CloudUploadOutlinedIcon />} disabled={uploading} onClick={() => doUpload(false)} sx={primaryBtnSx}>
            {uploading ? 'Uploading…' : 'Upload coverage'}
          </Button>
        </Stack>

        {result ? (
          <Box sx={{mt: 2.25, border: 1, borderColor: 'divider', borderRadius: '12px', p: 2, bgcolor: 'var(--row-alt, #F8FAFC)'}}>
            <Stack direction='row' alignItems='center' spacing={1} sx={{mb: 1.25}}>
              <Typography sx={{fontSize: 13, fontWeight: 800}}>{result._dryRun ? 'Dry-run result' : 'Upload result'}</Typography>
              <Badge status={result.status === 'COMPLETED' ? 'active' : 'info'} />
              <Typography sx={{fontSize: 12, color: 'text.secondary'}}>
                {result.direction} · {result.paymentMode}
                {result.replaceExisting ? ' · replace' : ''}
              </Typography>
            </Stack>
            <Stack direction='row' flexWrap='wrap' gap={2.5}>
              {[
                {k: 'In file', v: (result.pincodesInFile ?? 0).toLocaleString(), c: 'text.primary'},
                {k: 'Rows written', v: (result.rowsWritten ?? 0).toLocaleString(), c: '#15803d'},
                {k: 'Invalid lines', v: result.invalidLines ?? 0, c: result.invalidLines ? '#b45309' : 'text.primary'},
              ].map((m) => (
                <Box key={m.k}>
                  <Typography sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.secondary'}}>{m.k}</Typography>
                  <Typography sx={{fontSize: 20, fontWeight: 800, color: m.c, lineHeight: 1.2}}>{m.v}</Typography>
                </Box>
              ))}
            </Stack>
            {Array.isArray(result.errors) && result.errors.length ? (
              <Box sx={{mt: 1.5, fontSize: 12, color: '#b91c1c', bgcolor: '#fff', border: '1px solid #fecaca', borderRadius: '8px', p: 1.25}}>
                {result.errors.slice(0, 10).map((er, i) => (
                  <div key={i}>{er}</div>
                ))}
                {result.errors.length > 10 ? <div>…and {result.errors.length - 10} more</div> : null}
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>

      ) : null}

      {/* 2 — Check a lane */}
      {tab === 1 ? (
      <Box sx={cardSx}>
        <SectionHead icon={<FactCheckOutlinedIcon />} title='Check a lane' sub='Verify a pickup → drop pincode pair against the uploaded coverage.' />
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '150px 150px 1fr auto'}, gap: 2, alignItems: 'end', mb: lane || laneErr ? 2.25 : 0}}>
          <Labeled label='Pickup pincode'>
            <Box component='input' value={pickup} onChange={(e) => setPickup(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder='6-digit' sx={inputSx} />
          </Labeled>
          <Labeled label='Drop pincode'>
            <Box component='input' value={drop} onChange={(e) => setDrop(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder='6-digit' sx={inputSx} />
          </Labeled>
          <Labeled label='Account (optional)'>
            <SearchSelect placeholder='All serviceable accounts' value={checkAccount} onChange={setCheckAccount} options={accountCodes} sx={selectSx} />
          </Labeled>
          <Button variant='contained' size='small' disableElevation startIcon={<FactCheckOutlinedIcon />} disabled={checking} onClick={runCheck} sx={{...primaryBtnSx, height: FIELD_H}}>
            {checking ? 'Checking…' : 'Check'}
          </Button>
        </Box>

        {laneErr ? (
          <Box sx={{fontSize: 13, color: '#b91c1c'}}>{laneErr.message || 'Check failed.'}</Box>
        ) : lane ? (
          <Box>
            <Typography sx={{fontSize: 12.5, color: 'text.secondary', mb: 1}}>
              {lane.pickupPincode} → {lane.dropPincode}
            </Typography>
            <Box sx={{border: 1, borderColor: 'divider', borderRadius: '12px', overflow: 'hidden'}}>
              <Box sx={{...laneColsSx, bgcolor: 'var(--row-alt,#F8FAFC)', px: 1.75, py: 1.1, fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'text.secondary'}}>
                <span>Account</span><span>cp_id</span><span>Prepaid</span><span>COD</span>
              </Box>
              {(lane.results || []).length ? (
                lane.results.map((r, i) => (
                  <Box key={i} sx={{...laneColsSx, px: 1.75, py: 1.1, borderTop: 1, borderColor: 'divider', fontSize: 13}}>
                    <span style={{fontWeight: 600}}>{r.accountCode}</span>
                    <span>{r.cpId}</span>
                    <span><LaneCell ok={r.prepaid} determinable={r.prepaidDeterminable} /></span>
                    <span><LaneCell ok={r.cod} determinable={r.codDeterminable} /></span>
                  </Box>
                ))
              ) : (
                <Box sx={{px: 1.75, py: 2, fontSize: 13, color: 'text.secondary', borderTop: 1, borderColor: 'divider'}}>No serviceable accounts for this lane.</Box>
              )}
            </Box>
            {lane.serviceableByPaymentMode ? (
              <Stack direction='row' spacing={3} sx={{mt: 1.25, fontSize: 12.5, color: 'text.secondary'}}>
                <span>Prepaid accounts: <b>{(lane.serviceableByPaymentMode.PREPAID || []).length}</b></span>
                <span>COD accounts: <b>{(lane.serviceableByPaymentMode.COD || []).length}</b></span>
              </Stack>
            ) : null}
          </Box>
        ) : null}
      </Box>

      ) : null}

      {/* 3 — Account status & clear */}
      {tab === 2 ? (
      <Box sx={cardSx}>
        <SectionHead icon={<RuleFolderOutlinedIcon />} title='Account status & clear' sub='Whether an account is served from the coverage table, and clearing it.' />
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr auto auto'}, gap: 2, alignItems: 'end'}}>
          <Labeled label='Account'>
            <SearchSelect placeholder='Select an account' value={statusAccount} onChange={setStatusAccount} options={accountCodes} sx={selectSx} />
          </Labeled>
          <Button variant='outlined' size='small' disabled={!statusAccount || statusLoading} onClick={() => fetchStatus(statusAccount)} sx={{...outlinedBtnSx, height: FIELD_H}}>
            {statusLoading ? 'Checking…' : 'Check status'}
          </Button>
          <Button variant='outlined' color='error' size='small' startIcon={<DeleteOutlineOutlinedIcon />} disabled={!statusAccount || clearing} onClick={() => setConfirmClear(true)} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: 13, height: FIELD_H}}>
            {clearing ? 'Clearing…' : 'Clear coverage'}
          </Button>
        </Box>

        {status ? (
          <Stack direction='row' alignItems='center' spacing={1.5} sx={{mt: 2.25}}>
            <Badge status={status.loaded ? 'active' : 'norecord'} />
            <Typography sx={{fontSize: 13}}>
              {status.loaded ? (
                <>Coverage loaded — <b>{(status.row_count ?? 0).toLocaleString()}</b> pincode rows (table is authoritative).</>
              ) : (
                <>Not loaded — this account uses live serviceability checking.</>
              )}
            </Typography>
          </Stack>
        ) : null}
      </Box>

      ) : null}

      <ConfirmDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        title='Clear serviceability coverage?'
        okLabel='Clear coverage'
        danger
        body={`This deletes the uploaded pincode coverage for “${statusAccount}” and reverts it to live serviceability checking.`}
        bullets={['This cannot be undone — you would need to re-upload the CSV to restore it.']}
        onOk={doClear}
      />
    </>
  )
}
